import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, TrendingUp, Send, CheckCircle, Flame, Zap, Trophy, Gift, Rocket } from 'lucide-react';
import '../styles/employee-dashboard.css';

interface EmployeeDashboardProps {
  pointsEnabled: boolean;
  data: {
    earnings: {
      currentPoints: number;
      nextGoal: number | null;
      pointsToNextGoal: number;
      progressPercentage: number;
      hasActiveRewards: boolean;
      canClaimReward: boolean;
    };
    activity: {
      totalSubmitted: number;
      totalApproved: number;
      approvalRate: number;
    };
    streak: {
      current: number;
      isActive: boolean;
    };
    rewards: {
      totalClaimed: number;
      totalValue: number;
    };
    moments: {
      totalActiveMoments: number;
    }
  };
  userName: string;
}

export default function EmployeeDashboard({ data, userName, pointsEnabled }: EmployeeDashboardProps) {
  const navigate = useNavigate();
  const { moments, earnings, activity, streak, rewards } = data;

  return (
    <div className="dashboard-container employee-dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-greeting" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Hey, {userName} <Clapperboard size={28} color="#00bf62" />
        </h1>
        <p className="dashboard-subtitle">
          You have <span className="highlight-text">{moments.totalActiveMoments} active Moments</span> waiting for your magic.
        </p>
      </div>

      <div className={`employee-dashboard-grid${!pointsEnabled ? ' two-col' : ''}`}>
        {/* Earnings Progress Widget - Full Width, hidden when points disabled */}
        {pointsEnabled && <div className="employee-widget earnings-widget">
          {earnings.currentPoints === 0 && !earnings.canClaimReward ? (
            <>
              <div className="widget-header">
                <h3 className="widget-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TrendingUp size={20} color="#00bf62" /> Earnings Progress
                </h3>
              </div>
              <div className="zero-state">
                <p className="zero-state-title">Ready for your first bounty?</p>
                <button onClick={() => navigate('/moments')} className="zero-state-btn">
                  View Active Moments →
                </button>
              </div>
            </>
          ) : !earnings.hasActiveRewards ? (
            <>
              <div className="widget-header">
                <span className="earnings-label">Total Points</span>
              </div>
              <div className="earnings-value">{earnings.currentPoints.toLocaleString()} pts</div>
              <div className="progress-footer" style={{ marginTop: '16px' }}>
                <span className="progress-goal">Keep earning — rewards are coming soon!</span>
              </div>
            </>
          ) : earnings.canClaimReward ? (
            <>
              <div className="widget-header">
                <span className="earnings-label">Total Points</span>
              </div>
              <div className="earnings-value">{earnings.currentPoints.toLocaleString()} pts</div>

              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '100%' }} />
                </div>
                <div className="progress-labels">
                  <span>0</span>
                  <span>{earnings.nextGoal!.toLocaleString()}</span>
                </div>
              </div>

              <div className="progress-footer">
                <span className="progress-goal" style={{ color: '#00bf62', fontWeight: 600 }}>
                  You have enough points to claim a reward! <button onClick={() => navigate('/rewards')} className="zero-state-btn" style={{ display: 'inline', marginLeft: '8px', padding: '2px 10px', fontSize: '13px' }}>Claim Now →</button>
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="widget-header">
                <span className="earnings-label">Total Points</span>
              </div>
              <div className="earnings-value">{earnings.currentPoints.toLocaleString()} pts</div>

              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${Math.min(earnings.progressPercentage, 100)}%` }}
                  />
                </div>
                <div className="progress-labels">
                  <span>0</span>
                  <span>{earnings.nextGoal!.toLocaleString()}</span>
                </div>
              </div>

              <div className="progress-footer">
                <span className="progress-goal">
                  {earnings.pointsToNextGoal} pts until you can claim a Reward!
                </span>
              </div>
            </>
          )}
        </div>}

        {/* Activity Split Widget */}
        <div className="employee-widget activity-widget">
          <div className="widget-header">
            <h3 className="widget-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={20} color="#00bf62" /> Activity Split
            </h3>
          </div>
          
          {activity.totalSubmitted === 0 ? (
            <div className="zero-state">
              <p className="zero-state-title">No activity yet.</p>
              <button onClick={() => navigate('/moments')} className="zero-state-btn">
                Check your first Moment →
              </button>
            </div>
          ) : (
            <div className="activity-stats" onClick={() => navigate('/submissions')}>
              <div className="activity-stat">
                <div className="activity-icon submitted">
                  <Send size={20} color="#00bf62" />
                </div>
                <div className="activity-info">
                  <div className="activity-value">{activity.totalSubmitted}</div>
                  <div className="activity-label">Submitted</div>
                </div>
              </div>

              <div className="activity-stat">
                <div className="activity-icon approved">
                  <CheckCircle size={20} color="#00bf62" />
                </div>
                <div className="activity-info">
                  <div className="activity-value">{activity.totalApproved}</div>
                  <div className="activity-label">Approved</div>
                </div>
              </div>

              {activity.approvalRate > 0 && (
                <div className="approval-rate-badge">
                  {activity.approvalRate}% Approval Rate
                </div>
              )}
            </div>
          )}
        </div>

        {/* Streak Widget */}
        <div className={`employee-widget streak-widget ${streak.isActive ? 'streak-active' : ''}`}>
          <div className="widget-header">
            <h3 className="widget-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Flame size={20} color="#00bf62" /> Approval Streak
            </h3>
          </div>

          {streak.current === 0 ? (
            <div className="zero-state">
              <div className="streak-icon-gray">
                <Flame size={32} color="#666" />
              </div>
              <p className="zero-state-title">Start a new streak today!</p>
            </div>
          ) : (
            <>
              <div className="streak-value-container">
                <div className="streak-flame">
                  <Flame size={40} color="#FF6B35" />
                </div>
                <div className="streak-value">{streak.current}</div>
              </div>
              <div className="streak-label">
                {streak.current === 1 ? 'Week in a row' : 'Weeks in a row'}
              </div>
              {streak.isActive && (
                <div className="streak-encouragement" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Keep it going! <Zap size={16} color="#00bf62" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Rewards Claimed Widget — only shown when rewards are enabled */}
        {pointsEnabled && <div className="employee-widget rewards-claimed-widget">
          <div className="widget-header">
            <h3 className="widget-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Gift size={20} color="#00bf62" /> Rewards Claimed
            </h3>
          </div>

          {rewards.totalClaimed === 0 ? (
            <div className="zero-state">
              <p className="zero-state-title">Nothing here yet.</p>
              <p className="zero-state-subtitle">Turn your points into prizes!</p>
              <button onClick={() => navigate('/rewards')} className="zero-state-btn">
                Visit Rewards Shop →
              </button>
            </div>
          ) : (
            <div className="rewards-summary" onClick={() => navigate('/claimed-rewards')}>
              <div className="rewards-icon">
                <Trophy size={32} color="#00bf62" />
              </div>
              <div className="rewards-stats">
                <div className="rewards-value">{rewards.totalValue.toLocaleString()} pts</div>
                <div className="rewards-count">{rewards.totalClaimed} Rewards Total</div>
              </div>
              {rewards.totalValue > 5000 && (
                <div className="rewards-encouragement" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  You're on a roll! <Rocket size={16} color="#00bf62" />
                </div>
              )}
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

